import { Flex, rem, Stack, Text, Textarea } from "@mantine/core";
import { IconFileText, IconHistory } from "@tabler/icons-react";
import { useState, type ReactNode } from "react";
import Button from "@/design-system/components/Button";
import { useFormInputStyles } from "@/design-system/components/form/hooks/useFormInputStyles";
import { TabSectionHeader } from "../TabSectionHeader";
import PromptVersionsDrawer from "../PromptVersionsDrawer";
import classes from "./AgentForm.module.css";

export const INSTRUCTIONS_DESCRIPTION =
  "O papel do agente, o tom e o que ele pode ou não fazer na conversa.";

const PROMPT_MAX_LENGTH = 50000 as const;

interface AgentFormProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  /** Botão de salvar desta aba (nome + prompt), montado pela página. */
  headerAction?: ReactNode;
}

// O nome do agente saiu daqui: com a página em abas (SA-1351) ele é editado no
// próprio título. O salvar é da aba (`headerAction`), não da página.
export const AgentForm = ({
  prompt,
  onPromptChange,
  headerAction,
}: AgentFormProps) => {
  const { resolvedStyles } = useFormInputStyles({});
  // SA-1486: histórico de versões do prompt, só leitura.
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <Flex
      direction="column"
      gap="lg"
      w="100%"
      flex={1}
      className={classes.root}>
      <Stack
        gap={rem(8)}
        className={classes.field}>
        {/* Mesmo cabeçalho das outras abas (ícone, título, selo do que a IA faz). */}
        <TabSectionHeader
          icon={
            <IconFileText
              size={20}
              color="var(--mantine-color-R500-0)"
            />
          }
          title="Instruções do agente"
          badge="A IA SEGUE"
          description={INSTRUCTIONS_DESCRIPTION}
          action={
            // Só CSS: no celular o salvar ocupa a largura toda em cima e o
            // histórico vem embaixo. Decidir pelo JS deixava os dois lado a
            // lado e o salvar cortado enquanto o media query já era celular.
            <div className={classes.headerActions}>
              <Button
                variant="ghost"
                size="sm"
                className={classes.historyButton}
                leftSection={<IconHistory size={16} />}
                onClick={() => setHistoryOpen(true)}>
                Histórico de versões
              </Button>
              {headerAction}
            </div>
          }
        />

        {/* Ocupa toda a altura que sobra na página: prompt é texto longo e a
            rolagem acontece dentro do campo, não na página. */}
        <Textarea
          aria-label="Instruções do agente"
          value={prompt}
          onChange={(event) => {
            const value = event.currentTarget.value;
            onPromptChange(
              value.length > PROMPT_MAX_LENGTH
                ? value.slice(0, PROMPT_MAX_LENGTH)
                : value
            );
          }}
          size="md"
          styles={resolvedStyles}
          classNames={{
            root: classes.textareaRoot,
            wrapper: classes.textareaWrapper,
            input: classes.textareaInput,
          }}
        />
        <Text
          fz="xs"
          c="dimmed"
          ta="right">
          {prompt.length.toLocaleString("pt-BR")}/
          {PROMPT_MAX_LENGTH.toLocaleString("pt-BR")} caracteres
        </Text>
      </Stack>
      <PromptVersionsDrawer
        opened={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </Flex>
  );
};

export default AgentForm;
